"use client";

import Navbar from "@/components/ui/Navbar";
import Footer from "@/components/sections/Footer";
import { motion } from "framer-motion";
import Image from "next/image";
import { ArrowRight, ArrowUpRight, BarChart3, Brain, Globe, Layers, Zap, Server, Cpu } from "lucide-react";

export default function AiConsultancyPage() {
    return (
        <main className="min-h-screen bg-[#0a0a0a] text-white selection:bg-white/20 selection:text-white overflow-hidden font-sans">
            <Navbar />

            {/* ... existing sections ... */}

            {/* (This is just context, I will target the specific insertion point below) */}


            {/* New Hero Section (Verta Style) */}
            <section className="relative h-screen w-full overflow-hidden bg-[#050505] text-white">
                {/* Background Image */}
                <div className="absolute inset-0 z-0">
                    <Image
                        src="/aihero.jpg"
                        alt="Background"
                        fill
                        className="object-cover opacity-40"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/80" />
                </div>

                {/* Middle Text - Floating */}
                <div className="absolute top-1/2 left-0 w-full -translate-y-1/2 flex justify-between px-6 md:px-12 pointer-events-none hidden md:flex z-20">
                    <span className="text-xs text-gray-400 tracking-widest uppercase opacity-60">We don't predict the future</span>
                    <span className="text-xs text-gray-400 tracking-widest uppercase opacity-60">We build it for you</span>
                </div>

                {/* Content Container */}
                <div className="relative z-10 w-full h-full flex flex-col justify-between px-6 md:px-12 py-12">
                    {/* Spacer for Navbar */}
                    <div className="h-20" />

                    {/* Bottom Content */}
                    <div className="mt-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-end pb-12 md:pb-24">
                        {/* Left: Headline */}
                        <div>
                            <motion.h1
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8 }}
                                className="text-4xl md:text-5xl lg:text-7xl font-medium tracking-tighter leading-[0.9] lg:leading-[0.85] mb-6 md:mb-0"
                            >
                                Intelligent Solutions.<br />
                                Human Expertise.
                            </motion.h1>
                        </div>

                        {/* Right: Description & Buttons */}
                        <div className="flex flex-col gap-8 max-w-xl lg:ml-auto">
                            <motion.p
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.2 }}
                                className="text-lg md:text-xl text-gray-300 leading-relaxed"
                            >
                                Leading the way in AI-driven transformation. We help businesses integrate cutting-edge machine learning models into their core operations, driving efficiency and innovation.
                            </motion.p>

                        </div>
                    </div>
                </div>
            </section>




            {/* Algorithms Meet Aesthetics */}
            <section className="py-24 px-6 md:px-20 lg:px-40 bg-[#0f0f0f]">
                <div className="container mx-auto">
                    <div className="flex flex-col lg:flex-row justify-between items-start mb-24">
                        <h2 className="text-5xl md:text-7xl font-bold leading-tight max-w-3xl">
                            Where Algorithms<br />
                            Meet Innovation
                        </h2>
                        <div className="mt-8 lg:mt-0 max-w-sm">
                            <div className="px-4 py-2 border border-white/10 rounded-full inline-block mb-6 text-xs uppercase tracking-widest">The Power of AI</div>
                            <p className="text-gray-400 leading-relaxed">
                                Artificial Intelligence is redefining what's possible in business. By leveraging machine learning, neural networks, and automation, we help you transition from traditional operations to proactive, intelligent systems that scale.
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-6">
                        {/* 1. Custom AI Solutions Development - Large Card */}
                        <div className="lg:col-span-8 md:col-span-2 bg-[#111] rounded-[32px] p-8 md:p-12 border border-white/5 relative overflow-hidden group min-h-[400px] flex flex-col justify-end">
                            <Image
                                src="https://images.unsplash.com/photo-1620712943543-bcc4688e7485?q=80&w=2000&auto=format&fit=crop"
                                alt="Custom AI Solutions Development"
                                fill
                                className="object-cover opacity-40 group-hover:opacity-60 transition-opacity duration-700 mix-blend-overlay"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                            <div className="relative z-10">
                                <h3 className="text-3xl md:text-5xl font-semibold text-white mb-4 leading-tight">Custom AI Solutions <br />Development</h3>
                                <p className="text-gray-400 max-w-xl text-lg">Tailored artificial intelligence models designed specifically for your unique business challenges. We build from the ground up to ensure perfect alignment with your goals.</p>
                            </div>
                        </div>

                        {/* 2. Generative AI & LLM Solutions */}
                        <div className="lg:col-span-4 md:col-span-1 bg-[#111] rounded-[32px] p-6 md:p-8 border border-white/5 relative overflow-hidden group min-h-[250px] md:min-h-[400px] flex flex-col justify-between">
                            <div className="absolute top-0 right-0 p-6 md:p-8 transform translate-x-4 -translate-y-4 opacity-10 group-hover:opacity-30 group-hover:scale-110 transition-all duration-500">
                                <Brain className="w-24 h-24 md:w-40 md:h-40 text-white" />
                            </div>
                            <div className="relative z-10 mt-auto">
                                <h3 className="text-2xl md:text-3xl font-semibold text-white mb-4">Generative AI & LLM Solutions</h3>
                                <p className="text-gray-400 text-sm md:text-base">Empower your business with custom language models and generative tools that automate content creation and synthesize vast amounts of data.</p>
                            </div>
                        </div>

                        {/* 3. AI Automation & Workflow Optimization */}
                        <div className="lg:col-span-4 md:col-span-1 bg-[#111] rounded-[32px] p-6 md:p-8 border border-white/5 relative overflow-hidden group min-h-[250px] md:min-h-[400px] flex flex-col justify-between">
                            <div className="absolute top-0 right-0 p-6 md:p-8 transform translate-x-4 -translate-y-4 opacity-10 group-hover:opacity-30 group-hover:scale-110 transition-all duration-500">
                                <Zap className="w-24 h-24 md:w-40 md:h-40 text-white" />
                            </div>
                            <div className="relative z-10 mt-auto">
                                <h3 className="text-2xl md:text-3xl font-semibold text-white mb-4">AI Automation & Workflow</h3>
                                <p className="text-gray-400 text-sm md:text-base">Streamline operations by identifying bottlenecks and deploying intelligent agents that handle repetitive tasks, reducing human error.</p>
                            </div>
                        </div>

                        {/* 4. Computer Vision Solutions */}
                        <div className="lg:col-span-4 md:col-span-1 bg-gradient-to-br from-[#111] to-[#1a1525] rounded-[32px] p-6 md:p-8 border border-white/5 relative overflow-hidden group min-h-[250px] md:min-h-[400px] flex flex-col justify-between">
                            {/* Abstract visual for Computer Vision */}
                            <div className="absolute top-6 right-6 md:top-8 md:right-8 w-16 h-16 md:w-24 md:h-24 border-[1px] border-pink-500/20 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-500">
                                <div className="w-12 h-12 border-[1px] border-pink-400/40 rounded-full flex items-center justify-center">
                                    <div className="w-2 h-2 bg-pink-400/60 rounded-full"></div>
                                </div>
                            </div>
                            <div className="relative z-10 mt-auto">
                                <h3 className="text-2xl md:text-3xl font-semibold text-white mb-4">Computer Vision Solutions</h3>
                                <p className="text-gray-400 text-sm md:text-base">Extract meaningful insights from digital images and visual inputs. Implement object detection and automated quality control.</p>
                            </div>
                        </div>

                        {/* 5. Predictive Analytics & Data Science */}
                        <div className="lg:col-span-4 md:col-span-1 bg-[#111] rounded-[32px] p-6 md:p-8 border border-white/5 relative overflow-hidden group min-h-[250px] md:min-h-[400px] flex flex-col justify-between">
                            <div className="absolute top-0 right-0 p-6 md:p-8 transform translate-x-4 -translate-y-4 opacity-10 group-hover:opacity-30 group-hover:scale-110 transition-all duration-500">
                                <BarChart3 className="w-24 h-24 md:w-40 md:h-40 text-white" />
                            </div>
                            <div className="relative z-10 mt-auto">
                                <h3 className="text-2xl md:text-3xl font-semibold text-white mb-4">Predictive Analytics & Data Science</h3>
                                <p className="text-gray-400 text-sm md:text-base">Transform raw data into actionable foresight. We build models that forecast trends, predict customer behavior, and optimize decisions.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* AI Powers Creative */}
            <section className="py-24 px-6 md:px-20 lg:px-40 bg-black">
                <div className="container mx-auto">
                    <div className="flex flex-col lg:flex-row justify-between items-end mb-20">
                        <div>
                            <h2 className="text-5xl md:text-7xl font-bold leading-tight mb-6">
                                Where AI Powers<br />
                                the Future
                            </h2>
                        </div>
                        <div className="max-w-md text-right">
                            <p className="text-gray-400 leading-relaxed">
                                Kitebe is where strategic thinking meets machine learning. We bridge the gap between complex data and actionable business intelligence.
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-6">
                        {/* 1. Large Image Card */}
                        <div className="lg:col-span-5 bg-[#111] rounded-[32px] p-8 border border-white/5 relative overflow-hidden group min-h-[240px] md:min-h-[500px] flex flex-col justify-end">
                            <Image
                                src="https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?q=80&w=2564&auto=format&fit=crop"
                                alt="AI Creative"
                                fill
                                className="object-cover opacity-40 group-hover:opacity-60 transition-opacity duration-700 mix-blend-overlay"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                            <div className="relative z-10 mt-auto">
                                <h3 className="text-3xl font-semibold text-white mb-4">Strategic Thinking</h3>
                                <p className="text-gray-400 text-lg md:text-xl">Where complex data meets actionable business intelligence.</p>
                            </div>
                        </div>

                        {/* Mid & Right Stats / Content */}
                        <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6">
                            {/* Stat 1 */}
                            <div className="bg-[#111] rounded-[32px] p-8 border border-white/5 relative overflow-hidden group flex flex-col justify-center">
                                <span className="text-5xl font-bold text-white mb-4">10.5x</span>
                                <p className="text-gray-400 text-sm font-mono uppercase tracking-widest">Automated <br />processes</p>
                            </div>
                            {/* Stat 2 */}
                            <div className="bg-[#111] rounded-[32px] p-8 border border-white/5 relative overflow-hidden group flex flex-col justify-center">
                                <span className="text-5xl font-bold text-white mb-4">98%</span>
                                <p className="text-gray-400 text-sm font-mono uppercase tracking-widest">Trusted by <br />businesses</p>
                            </div>

                            {/* Intelligent Infrastructure */}
                            <div className="bg-[#111] rounded-[32px] p-8 md:p-10 border border-white/5 relative overflow-hidden group min-h-[250px] flex flex-col justify-between">
                                <div className="absolute top-0 right-0 p-8 transform translate-x-4 -translate-y-4 opacity-10 group-hover:opacity-30 group-hover:scale-110 transition-all duration-500">
                                    <Server className="w-24 h-24 md:w-32 md:h-32 text-white" />
                                </div>
                                <div className="relative z-10 mt-auto">
                                    <h3 className="text-2xl font-semibold text-white mb-3">Intelligent Systems</h3>
                                    <p className="text-gray-400 text-sm md:text-base">Build robust, scalable AI architectures that adapt to your growing business needs and data volumes.</p>
                                </div>
                            </div>

                            {/* Proprietary AI Models */}
                            <div className="bg-[#111] rounded-[32px] p-8 md:p-10 border border-white/5 relative overflow-hidden group min-h-[250px] flex flex-col justify-between">
                                <div className="absolute top-0 right-0 p-8 transform translate-x-4 -translate-y-4 opacity-10 group-hover:opacity-30 group-hover:scale-110 transition-all duration-500">
                                    <Cpu className="w-24 h-24 md:w-32 md:h-32 text-white" />
                                </div>
                                <div className="relative z-10 mt-auto">
                                    <h3 className="text-2xl font-semibold text-white mb-3">Proprietary AI</h3>
                                    <p className="text-gray-400 text-sm md:text-base">Deploy custom machine learning models trained on your specific data to gain a competitive edge.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Transforming Ideas Section */}
            <section className="py-24 px-6 md:px-20 lg:px-40 bg-white text-black relative z-10" data-header-theme="dark">
                <div className="container mx-auto">
                    {/* Header */}
                    <div className="flex flex-col lg:flex-row justify-between items-start mb-20 gap-12">
                        <div className="max-w-sm">
                            <p className="text-gray-600 mb-8 leading-relaxed">
                                Our approach combines technical excellence with deep strategic insight, ensuring that every AI implementation delivers measurable value.
                            </p>

                        </div>
                        <div className="max-w-2xl text-right">
                            <h2 className="text-5xl md:text-7xl font-bold leading-[0.9] tracking-tight">
                                Transforming <span className="inline-block align-middle"><Globe className="w-12 h-12 md:w-16 md:h-16" strokeWidth={1} /></span><br />
                                data into strategic<br />
                                business advantages
                            </h2>
                        </div>
                    </div>

                    {/* Content Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Left Card */}
                        <div className="relative p-1 bg-gradient-to-br from-purple-600 via-blue-500 to-pink-500 rounded-[40px] overflow-hidden">
                            <div className="bg-white rounded-[36px] p-8 md:p-12 h-full flex flex-col justify-between relative">
                                <div className="flex justify-between items-start mb-12">
                                    <div className="w-16 h-16 md:w-20 md:h-20 bg-[#1a1a1a] rounded-full flex items-center justify-center text-white">
                                        <ArrowUpRight className="w-6 h-6 md:w-8 md:h-8" />
                                    </div>
                                    <div className="flex gap-2">
                                        <span className="px-4 py-2 border border-gray-200 rounded-full text-sm">Our Service</span>
                                        <span className="px-4 py-2 border border-gray-200 rounded-full text-sm">2024</span>
                                    </div>
                                </div>

                                <div>
                                    <h3 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                                        150+ Enterprises<br />optimized
                                    </h3>
                                    <p className="text-gray-500 mb-8">We deliver precision-engineered AI solutions for modern businesses.</p>
                                    <button className="px-8 py-4 border border-black rounded-full font-bold hover:bg-black hover:text-white transition-colors">
                                        Get Started Now
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Right List */}
                        <div className="flex flex-col gap-4">
                            {[
                                { title: "Strategy", desc: "We develop comprehensive AI roadmaps tailored to your specific business objectives and industry challenges.", active: false },
                                { title: "Integration", desc: "Seamlessly incorporate AI capabilities into your existing software stack and business processes.", active: true },
                                { title: "Custom Models", desc: "Train and deploy proprietary machine learning models optimized for your unique datasets.", active: false },
                                { title: "Optimization", desc: "Continuously refine and enhance your AI systems to maintain peak performance and ROI.", active: false }
                            ].map((item, i) => (
                                <div key={i} className={`p-8 rounded-[30px] flex flex-col md:flex-row gap-8 items-start transition-all ${item.active ? 'bg-[#6B4BCC] text-white' : 'bg-gray-50 text-black hover:bg-gray-100'}`}>
                                    <div className="whitespace-nowrap">
                                        <span className={`px-6 py-3 rounded-full text-sm font-bold inline-flex items-center gap-2 ${item.active ? 'bg-white/20' : 'bg-white text-black'}`}>
                                            {item.title}
                                            <ArrowUpRight className="w-4 h-4" />
                                        </span>
                                    </div>
                                    <p className={`text-sm leading-relaxed ${item.active ? 'text-white/80' : 'text-gray-500'}`}>
                                        {item.desc}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Our Methodology Section */}
            <section className="py-24 px-6 md:px-20 lg:px-40 bg-white text-black border-t border-gray-200 selection:bg-purple-600 selection:text-white" data-header-theme="dark">
                <div className="container mx-auto">
                    <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-8">
                        <div className="max-w-xl">
                            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight tracking-tight">
                                A systematic approach to <br /> intelligent automation
                            </h2>
                        </div>
                        <p className="text-gray-600 max-w-sm mb-2 md:text-right">
                            We don't just write code. We analyze, strategize, build, and scale alongside your team to guarantee long-term success.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-gray-200 border border-gray-200 rounded-[32px] overflow-hidden">
                        {[
                            {
                                step: "01",
                                title: "Discovery & Analysis",
                                desc: "We dive deep into your data landscape and business objectives to identify high-impact opportunities for AI integration."
                            },
                            {
                                step: "02",
                                title: "Architecture & Development",
                                desc: "Our engineers design scalable, secure ML pipelines and intelligent agents tailored specifically to your operational needs."
                            },
                            {
                                step: "03",
                                title: "Deployment & Scaling",
                                desc: "We ensure seamless deployment with your existing systems, providing continuous monitoring and optimization for peak efficiency."
                            }
                        ].map((item, index) => (
                            <div key={index} className="bg-white p-10 md:p-12 group hover:bg-gray-50 transition-colors duration-300">
                                <span className="text-5xl font-light text-gray-200 mb-8 block group-hover:text-purple-600 transition-colors duration-300">{item.step}</span>
                                <h3 className="text-2xl font-semibold mb-4 text-black">{item.title}</h3>
                                <p className="text-gray-600 leading-relaxed text-sm">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <Footer />
        </main>
    );
}
